import { useState, MouseEvent, ReactNode, CSSProperties } from 'react';

interface GlowButtonProps {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  as?: 'button' | 'a';
  style?: CSSProperties;
  /**
   * primary  — 蓝色毛玻璃，白色文字（默认）
   * secondary — 白色毛玻璃，深色文字，用于次要操作
   */
  variant?: 'primary' | 'secondary';
}

export default function GlowButton({
  children,
  onClick,
  href,
  target,
  rel,
  className = '',
  type = 'button',
  disabled = false,
  as = 'button',
  style,
  variant = 'primary',
}: GlowButtonProps) {
  const [hovered, setHovered] = useState(false);

  const isPrimary = variant === 'primary';

  // primary: 蓝色毛玻璃
  const primaryBase = 'linear-gradient(135deg, rgba(0,168,240,0.48) 0%, rgba(0,110,180,0.58) 100%)';
  const primaryHover = 'linear-gradient(135deg, rgba(0,148,218,0.78) 0%, rgba(0,90,160,0.88) 100%)';

  // secondary: 白色/浅蓝毛玻璃
  const secondaryBase = 'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(210,230,248,0.20) 100%)';
  const secondaryHover = 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(210,230,248,0.45) 100%)';

  const bgGradient = isPrimary
    ? (hovered ? primaryHover : primaryBase)
    : (hovered ? secondaryHover : secondaryBase);

  const computedStyle: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    background: bgGradient,
    backdropFilter: 'blur(32px) saturate(220%) brightness(1.08)',
    WebkitBackdropFilter: 'blur(32px) saturate(220%) brightness(1.08)',
    border: 'none',
    outline: 'none',
    boxShadow: isPrimary
      ? (hovered
          ? '0 8px 28px rgba(0,136,204,0.28), inset 0 1px 0 rgba(255,255,255,0.18)'
          : '0 2px 12px rgba(0,136,204,0.10), inset 0 1px 0 rgba(255,255,255,0.40)')
      : (hovered
          ? '0 4px 14px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.55)'
          : '0 1px 4px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.40)'),
    transition: 'background 0.28s ease, box-shadow 0.28s ease',
    ...style,
  };

  // primary → 白色文字；secondary → 深灰文字
  const textColor = isPrimary ? 'text-white' : 'text-slate-700';

  const sharedClassName = `inline-flex items-center justify-center font-semibold cursor-pointer whitespace-nowrap select-none ${textColor} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`;

  // 高光线颜色随 variant 调整
  const topHighlight = isPrimary
    ? (hovered
        ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent)')
    : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.80), transparent)';

  const content = (
    <>
      {/* 顶部折射高光线 */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: '8%',
          right: '8%',
          height: '1px',
          background: topHighlight,
          pointerEvents: 'none',
          transition: 'background 0.28s ease',
        }}
      />
      {/* 底部微弱反光线 */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 0,
          left: '20%',
          right: '20%',
          height: '1px',
          background: isPrimary
            ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.30), transparent)',
          pointerEvents: 'none',
        }}
      />
      {/* 左侧竖向高光 */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '15%',
          bottom: '15%',
          left: 0,
          width: '1px',
          background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.30), transparent)',
          pointerEvents: 'none',
        }}
      />
      {/* 磨砂噪点纹理 */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          opacity: hovered ? 0.03 : 0.06,
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
          backgroundSize: '120px 120px',
          pointerEvents: 'none',
          transition: 'opacity 0.28s ease',
        }}
      />
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </>
  );

  if (as === 'a' && href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        style={computedStyle}
        className={sharedClassName}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={computedStyle}
      className={sharedClassName}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {content}
    </button>
  );
}
